import { BaseHub, BaseHubInitOptions, BaseHubClientMethod, BaseHubMethods } from "@common/stores/hubs/BaseHub";
import { makeObservable, observable } from "mobx";
import { ContextWrapper } from "@common/contexts";
import { AppointmentStatus, CommunicationEventType } from "@common/models/Enums";
import { ClientDocumentRequestModel } from "@common/models/ClientDocumentRequestModel";


export type HospitalHubMethods = BaseHubMethods & {
    joinHospitalGroup: (hospitalId: number) => void;
    leaveHospitalGroup: (hospitalId: number) => void;
}

export type HospitalHubClientMethod = BaseHubClientMethod |
{ eventName: "onJoinHospitalGroup", callback: (connectionId: string, hospitalId: number, userName: string) => void }
    | { eventName: "onLeaveHospitalGroup", callback: (connectionId: string, hospitalId: number, userName: string) => void }
    | { eventName: "onHospitalTaskUpdated", callback: (taskId: number, hospitalId: number, userIds: number[], roleName: string) => void }
    | { eventName: "onConnected", callback: () => void }
    | { eventName: "onUpdatedHospital", callback: (hospitalId: number) => void }
    | { eventName: "onAppointmentUpdated", callback: (hospitalId: number, reservationId: number | null, visitId: number | null, strStartDate: string, strEndDate: string) => void }
    | { eventName: "onAppointmentStatusChanged", callback: (hospitalId: number, reservationId: number | null, visitId: number | null, apptStatus: AppointmentStatus, strStartDate: string, strEndDate: string) => void }
    | { eventName: "onAppointmentRescheduled", callback: (hospitalId: number, reservationId: number | null, visitId: number | null, strStartDate: string, strEndDate: string) => void }
    | { eventName: "onAppointmentReopened", callback: (hospitalId: number, reservationId: number | null, visitId: number | null, strStartDate: string, strEndDate: string) => void }
    | { eventName: "onUpdatedHospitalSchedule", callback: (hospitalId: number, strChangeDates: string[]) => void }
    | { eventName: "onUpdatedHospitalScheduleAdHoc", callback: (hospitalId: number, strScheduleDate: string | null, scheduleId: number) => void }
    | { eventName: "onUpdatedHospitalScheduleOverrides", callback: (hospitalId: number, strDateScheduled: string) => void }
    | { eventName: "onUpdatedHospitalScheduleTemplate", callback: (hospitalId: number, templateId: number, templateName: string) => void }
    | { eventName: "onAppointmentBookedOnline", callback: (hospitalId: number, strScheduleDate: string | null) => void }
    | { eventName: "onUnreadHospitalMessagesUpdated", callback: (storeId: number, unreadMessageCount: number, unreadMessageClientCount: number, communicationEventType: CommunicationEventType) => void }
    | { eventName: "onDocumentGenerationStatusUpdated", callback: (clientDocumentRequestModel: ClientDocumentRequestModel) => void }

export class HospitalHubStore extends BaseHub {
    private static _currentInstance: HospitalHubStore | undefined;
    @observable private _loggedInHospitalId: number | undefined = undefined;
    protected constructor(options: Omit<BaseHubInitOptions, "hubName">) {
        super({ hubName: "hospitalHub", ...options });
        makeObservable(this, undefined);
        (globalThis as any)["hospitalHub"] = this;
    }

    public static get currentInstance() {
        if (HospitalHubStore._currentInstance === undefined)
            HospitalHubStore._currentInstance = new HospitalHubStore({ isMock: appEnv.USEMOCK, env: appEnv.ENV });
        return HospitalHubStore._currentInstance;
    }

    //#region Transmit Methods
    /**
     * Invokes the server side hub to cause this signalr client to be added to the hospital group for the hospitalId.  this effectively makes this connection an active user of the specified hospital
     */
    joinHospitalGroup = async (hospitalId: number) => {
        await this.safeInvoke<HospitalHubMethods, "leaveHospitalGroup", boolean>("leaveHospitalGroup", hospitalId);
        let joinedgroup = await this.safeInvoke<HospitalHubMethods, "joinHospitalGroup", boolean>("joinHospitalGroup", hospitalId);
        return joinedgroup
    };

    /**
     * Invokes the server side hub to cause this signalr client to be removed from teh hoospital group for the hospitalId, this makes the user no longer actively connected to that hospital group
     */
    leaveHospitalGroup = async (hospitalId: number) => {
        var leftGroup = await this.safeInvoke<HospitalHubMethods, "leaveHospitalGroup", void>("leaveHospitalGroup", hospitalId);
        return leftGroup;
    };

    //#endregion

    //#region Base Methods

    public override registerMethod(event: HospitalHubClientMethod): void {
        super.registerMethod(event);
    }

    public override unregisterMethod(event: HospitalHubClientMethod): void {
        super.unregisterMethod(event);
    }
    override getExtraQueueMethodsToClean() {
        return ["joinHospitalGroup"];
    }
    //#endregion
}

export const HospitalHubStoreContext = new ContextWrapper<HospitalHubStore>();
